import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import BrandLogo from "@/components/brand/BrandLogo";
import { NAV_ICONS } from "@/config/navIcons";
import { platformNavigation } from "@/config/platformNavigation";
import { usePlatformAccess } from "@/hooks/usePlatformAccess";
import { usePlatformSessionStore } from "@/store/platformSession.store";
import { primaryTintBg } from "@/theme/surfaces";

function PlatformSidebar({ onNavigate }) {
  const { t } = useTranslation();
  const location = useLocation();
  const operator = usePlatformSessionStore((s) => s.operator);
  const { can } = usePlatformAccess();
  const items = platformNavigation.filter((item) => can(item.permission));

  return (
    <Box
      component="nav"
      aria-label="Platform navigation"
      sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Box sx={{ pb: 1.5, pt: 0.5, minHeight: 52 }}>
        <BrandLogo />
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1,
            fontWeight: 800,
            color: "primary.main",
            letterSpacing: "0.06em",
          }}
        >
          {t("platform.consoleLabel")}
        </Typography>
      </Box>
      <Divider sx={{ my: 1 }} />
      <List dense sx={{ flex: 1 }}>
        {items.map((item) => {
          const active =
            location.pathname === item.href ||
            location.pathname.startsWith(`${item.href}/`);
          const Icon = NAV_ICONS[item.iconKey];
          return (
            <ListItemButton
              key={item.href}
              component={NavLink}
              to={item.href}
              onClick={onNavigate}
              selected={active}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                py: 0.9,
                color: "text.secondary",
                "&.Mui-selected": {
                  color: "primary.main",
                  bgcolor: (th) => primaryTintBg(th, true),
                  fontWeight: 700,
                  "& .MuiListItemIcon-root": { color: "primary.main" },
                },
                "&:hover": {
                  bgcolor: (th) =>
                    th.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
                {Icon ? <Icon fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText
                primary={t(item.navId, { defaultValue: item.name })}
                primaryTypographyProps={{
                  fontSize: "0.86rem",
                  fontWeight: 600,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Divider />
      <Box
        sx={{
          pt: 2,
          display: "flex",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: (th) => primaryTintBg(th, true),
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {operator?.name
            ?.split(" ")
            .map((p) => p[0])
            .join("")
            .slice(0, 2) ?? "OP"}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {operator?.name ?? t("platform.operatorFallback")}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {operator?.email ?? t("platform.consoleLabel")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default PlatformSidebar;
